import React, { useRef, useState } from 'react';
import { Button, Modal, Container, Row, Col, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  useAppContext,
  callNativeMobileApp,
  useTranslator,
  useToast,
} from '@front-activerse/common-components';
import { normalizeBarcodeMeal } from '../helpers/normalizers';
import { useBarcodeScanner } from '../hooks';
import { withForcedAiError } from '../helpers/devForcedError';
import Lottie from 'lottie-react';

interface ScanBarCodeContainerProps {
  scanBarcode: (ean: string) => Promise<unknown>;
  cancelScanBarcode: () => void;
  fromUserMeals?: boolean;
}

const ScanBarCodeContainer: React.FC<ScanBarCodeContainerProps> = ({
  scanBarcode,
  fromUserMeals,
}) => {
  const navigate = useNavigate();
  const isAnalyzingRef = useRef(false);

  const [noFoodError, setNoFoodError] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const [productNotFoundError, setProductNotFoundError] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [lastScannedEan, setLastScannedEan] = useState<string>('');

  const { toastError } = useToast();
  const { t } = useTranslator();

  const {
    lotties: { loaderStars },
    images: {
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      infoWhiteIcon,
      trackerScanBarCodeTipsImg,
      videoPoster,
    },
  } = useAppContext();

  const handleScanSuccess = async (decodedText: string) => {
    if (isAnalyzingRef.current) return;

    setLastScannedEan(decodedText);
    isAnalyzingRef.current = true;
    setNoFoodError(false);
    setRateLimitExceeded(false);
    setProductNotFoundError(false);
    setIsAnalyzing(true);
    try {
      const response: any = withForcedAiError(await scanBarcode(decodedText));

      if (response.__typename === 'AnalyzedDailyTrackerIngredients') {
        await stop();

        sessionStorage.setItem('tracker_depth', '2');

        const dietMealId = Math.floor(Math.random() * 1000000000);

        const normalizedProps = normalizeBarcodeMeal({
          ...response,
          dietMealId,
        });

        const basePath = fromUserMeals
          ? '/meals/user-meals/describe-meal/meal-details'
          : '/tracker/describe-meal/meal-details';

        navigate(`${basePath}/${dietMealId}?isBarcode=true`, {
          state: {
            mealData: normalizedProps.mealData,
            requestId: response.requestId,
            timestamp: Date.now(),
          },
        });
      } else if (response.__typename === 'NotFoodError') {
        setNoFoodError(true);
      } else if (response.__typename === 'RateLimitExceeded') {
        setRateLimitExceeded(true);
      } else if (response.__typename === 'ProductNotFoundError') {
        setProductNotFoundError(true);
      } else {
        toastError();
      }
    } catch {
      toastError();
    } finally {
      isAnalyzingRef.current = false;
      setIsAnalyzing(false);
    }
  };

  const handleCloseClick = async () => {
    await stop();
    await new Promise((r) => setTimeout(r, 100));

    callNativeMobileApp({ action: 'close_modal' });
    navigate(-1);
  };

  const {
    videoRef,
    isCameraActive,
    isScanning,
    isSearchingCamera,
    isTorchSupported,
    isTorchOn,
    error,
    toggleTorch,
    stop,
  } = useBarcodeScanner({
    onDetected: handleScanSuccess,
    onReleaseRequest: handleCloseClick,
    restartOnPopstatePath: '/tracker/scan-barcode',
  });

  const toggleModal = () => setShowModal(!showModal);

  return (
    <>
      <div className="scan-plate">
        <div className="barcode-reader-container">
          <video ref={videoRef} poster={videoPoster} playsInline muted />
        </div>

        <div
          className={
            isAnalyzing
              ? 'camera-overlay-fullscreen-barcode-lottie'
              : 'camera-overlay-fullscreen-barcode'
          }
        >
          {isCameraActive && !isAnalyzing && <div className="scan-area-barcode" />}

          {isAnalyzing && (
            <div className="scan-area-barcode-lottie">
              <Lottie animationData={loaderStars} loop={true} />
              <p>{t('tracker.describeFood.loader.calculating')}</p>
            </div>
          )}

          <div className="top-controls">
            <button className="scan-plate-button" onClick={handleCloseClick}>
              <img src={closeWhiteIcon} alt="" />
            </button>
            <button className="scan-plate-button" onClick={toggleModal}>
              <img src={infoWhiteIcon} alt="" />
            </button>
          </div>

          {!isAnalyzing && (
            <div
              className={
                noFoodError || rateLimitExceeded || productNotFoundError || error
                  ? 'bottom-controls justify-content-center'
                  : 'bottom-controls'
              }
            >
              {noFoodError || rateLimitExceeded || productNotFoundError || error ? (
                <>
                  {noFoodError && (
                    <Alert color="warning" className="mb-0" fade={false}>
                      <p className="mb-0 text-text-main-color text-start">
                        {t('tracker.describeFood.analyze.noFood.notification')}
                      </p>
                    </Alert>
                  )}

                  {rateLimitExceeded && (
                    <Alert color="danger" className="mb-0" fade={false}>
                      <p className="mb-0 text-text-main-color text-start">
                        {t('tracker.limitUsageError.notification')}
                      </p>
                    </Alert>
                  )}

                  {productNotFoundError && (
                    <Alert
                      color="warning"
                      className="mb-0"
                      style={{ marginTop: '-60px' }}
                      fade={false}
                    >
                      <p className="text-text-main-color text-start">
                        {t('tracker.scanFood.analyze.productNotFound.notification')}
                      </p>

                      <div className="text-center">
                        <Button
                          color="link"
                          size="sm"
                          onClick={async () => {
                            // Release the camera before leaving the scanner.
                            await stop();
                            // No meal in progress here (standalone scanner): tell add-product to
                            // build a new meal from the saved product and open the meal editor,
                            // instead of navigating back onto this scanner.
                            navigate('/tracker/add-product', {
                              state: {
                                ean: lastScannedEan,
                                createMealFromProduct: true,
                                fromUserMeals,
                                // The product is typed by hand, but the flow was entered by
                                // scanning a code — that is the source the meal reports.
                                aiDescriptionSource: 'fetch_product_by_ean',
                              },
                            });
                          }}
                          data-testid="add-product-from-barcode"
                          data-test-id="add_product_from_barcode"
                        >
                          <span className="semi-bold">
                            {t(
                              'tracker.scanFood.analyze.productNotFound.notification.addProductManuallybutton'
                            )}
                          </span>
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {error && (
                    <div className="text-center">
                      <Alert color="danger" className="mb-0" fade={false}>
                        <p className="mb-0 text-text-main-color text-start">
                          {t('tracker.scanFood.noAccessCamera.notification')}
                        </p>
                      </Alert>
                      {!isCameraActive && !isSearchingCamera && (
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => window.location.reload()}
                          disabled={isSearchingCamera || isScanning}
                        >
                          <p>{t('tracker.scan.button.refreshCamera')}</p>
                        </Button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {isCameraActive && isTorchSupported ? (
                    <button
                      className="scan-plate-button"
                      onClick={toggleTorch}
                      disabled={isScanning}
                    >
                      <img src={isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                    </button>
                  ) : (
                    <button className="scan-plate-button" disabled>
                      <img src={flashOffIcon} alt="" />
                    </button>
                  )}

                  {!isCameraActive && isSearchingCamera && (
                    <p>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t('tracker.scan.findingCamera')}
                    </p>
                  )}

                  {isCameraActive && !isSearchingCamera && (
                    <p>{t('tracker.scan.barcodeDescription')}</p>
                  )}

                  <div style={{ width: '40px', height: '40px' }} />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <Container>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <img className="img-fluid" src={trackerScanBarCodeTipsImg} alt="" />
                <h2 className="title-medium bold">{t('tracker.tipsModal.title')}</h2>
                <p className="text-text-variant-color mb-xl">
                  {t('tracker.tipsModal.description.barcode')}
                </p>

                <Button color="primary" className="w-100" onClick={toggleModal}>
                  {t('tracker.tipsModal.button')}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

export default ScanBarCodeContainer;
